import { Inject, Injectable, Optional } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { WorkloadQueryParameter } from '../shared/constants/workload-query-parameter.enum';
import { WorkloadQueryParameterToken } from '../shared/constants/workload-query-parameter.injection-token';
import { DocumentService } from '../shared/services/document.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { WindowService } from '../shared/services/window.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ServiceWorkerRegistrationHandler, SERVICE_WORKER_REGISTRATION_HANDLERS } from './registration-handlers/service-worker-registration-handlers.injection-token';
import { SERVICE_WORKER_API_AVAILABLE } from './service-worker-api-available.injection-token';
import { ServiceWorkerConfigService } from './service-worker-config.service';
import { SERVICE_WORKER_ENABLED } from './service-worker-enabled.injection-token';
import { SERVICE_WORKER_GET_REGISTRATION_BY_SCOPE } from './service-worker-get-registration-by-scope.injection-token';
import { ServiceWorkerInitializer } from './service-worker-initializer.service';
import { ServiceWorkerStateService } from './service-worker-state.service';

@Injectable({
    providedIn: 'root'
})
export class DelayedServiceWorkerInitializer extends ServiceWorkerInitializer {
    constructor(
        serviceWorkerConfigService: ServiceWorkerConfigService,
        @Inject(SERVICE_WORKER_ENABLED) serviceWorkerEnabled: boolean,
        @Inject(SERVICE_WORKER_API_AVAILABLE) hasServiceWorkerApi,
        windowService: WindowService,
        startupConfigService: ClientStartupConfigService,
        globalErrorHandler: OfficeHomePageErrorHandler,
        @Inject(WorkloadQueryParameterToken) workloadQueryParameterValue: WorkloadQueryParameter,
        officeHomeApiConfig: OfficeHomeApiConfigService,
        serviceWorkerStateService: ServiceWorkerStateService,
        documentService: DocumentService,
        private readonly schedulerService: SchedulerService,
        @Optional() @Inject(SERVICE_WORKER_REGISTRATION_HANDLERS) registrationHandlers?: Array<ServiceWorkerRegistrationHandler>,
        @Optional() @Inject(SERVICE_WORKER_GET_REGISTRATION_BY_SCOPE) getRegistrationByScope?: boolean
    ) {
        super(serviceWorkerConfigService, serviceWorkerEnabled, hasServiceWorkerApi, windowService, startupConfigService, globalErrorHandler, workloadQueryParameterValue, officeHomeApiConfig, serviceWorkerStateService, documentService, registrationHandlers, getRegistrationByScope);
    }

    protected registerServiceWorker(scriptUrl: string, scope: string): Promise<ServiceWorkerRegistration> {
        return new Promise(resolve => {
            const registerSW = () => {
                this.schedulerService.scheduleWork(() => {
                    resolve(this.serviceWorkerContainer.register(scriptUrl, { scope }));
                }, SchedulePriority.VeryLow);
                this.windowService.nativeWindow.removeEventListener('load', registerSW);
            };
            // Register the worker if the load event already happened
            if (this.documentService.nativeDocument.readyState === 'complete') {
                registerSW();
            } else {
                this.windowService.nativeWindow.addEventListener('load', registerSW);
            }
        });
    }
}